import React from 'react';
import { FileText } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { FenetreEmailsModule } from '../ui/LigneNotification';
import { Segmented } from '../ui/Field';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons RÉELLEMENT disponibles dans chaque email (cf. `vars` construits dans sendPrestationDevisEmail /
// notifierEtablissementDevis, functions/api/[[path]].js) — jamais la liste générique ALL_NOTIFICATION_TOKENS,
// qui propose des variables sans rapport (salarié assigné, rendez-vous...) et ne contient pas {{bouton}}.
const DEVIS_CLIENT_TOKENS = [
    { token: '{{nom}}', label: 'Nom du client' },
    { token: '{{prenom}}', label: 'Prénom du client' },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{montant_devis}}', label: 'Montant du devis' },
    { token: '{{bouton}}', label: 'Bouton "Consulter et signer" (cliquez où l\'insérer)' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const DEVIS_SIGNED_TOKENS = [
    { token: '{{nom}}', label: 'Nom du client' },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{montant_devis}}', label: 'Montant du devis' },
];
const DEVIS_DECLINED_TOKENS = [
    ...DEVIS_SIGNED_TOKENS,
    { token: '{{motif}}', label: 'Motif indiqué par le client' },
];

/**
 * Réglages des emails du module Devis, ouverts DIRECTEMENT depuis le module (bouton « Paramètres » de
 * DevisPrestationModule.tsx) plutôt que de renvoyer vers Paramètres > Notifications — demande explicite
 * d'Emeni (2026-09-03) : chaque module doit porter ses propres réglages d'email pour éviter l'aller-retour.
 * Deux onglets (Client / Établissement, autre demande d'Emeni) : le premier mail part vers le client à
 * l'envoi du devis, les deux autres reviennent vers l'établissement quand le client répond.
 * Les champs restent les mêmes colonnes que la page centrale (prestation_notification_settings.devis_* et
 * client_notification_settings.devis_signed_received_... / devis_declined_...) — deux endroits différents
 * ici n'affichent que ce sous-ensemble, mais l'enregistrement renvoie l'objet complet à chaque fois (les
 * deux routes PUT attendent tous les champs, pas un patch partiel).
 */
export function DevisEmailSettingsModal({ onClose, embarque = false }: { onClose: () => void; embarque?: boolean }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [onglet, setOnglet] = React.useState<'client' | 'etablissement'>('client');

    const [devisSubject, setDevisSubject] = React.useState('');
    const [devisBody, setDevisBody] = React.useState('');
    const [devisButtonLabel, setDevisButtonLabel] = React.useState('');
    const [devisActiveField, setDevisActiveField] = React.useState<'subject' | 'body'>('body');
    const devisSubjectRef = React.useRef<HTMLInputElement>(null);
    const devisBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifySigned, setNotifySigned] = React.useState(true);
    const [signedSubject, setSignedSubject] = React.useState('');
    const [signedBody, setSignedBody] = React.useState('');
    const [signedActiveField, setSignedActiveField] = React.useState<'subject' | 'body'>('body');
    const signedSubjectRef = React.useRef<HTMLInputElement>(null);
    const signedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyDeclined, setNotifyDeclined] = React.useState(true);
    // Relance d'un devis sans réponse — mail distinct de l'envoi initial (2026-09-21, Emeni).
    const [relanceSubject, setRelanceSubject] = React.useState('');
    const [relanceBody, setRelanceBody] = React.useState('');
    const [relanceActiveField, setRelanceActiveField] = React.useState<'subject' | 'body'>('body');
    const relanceSubjectRef = React.useRef<HTMLInputElement>(null);
    const relanceBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const [declinedSubject, setDeclinedSubject] = React.useState('');
    const [declinedBody, setDeclinedBody] = React.useState('');
    const [declinedActiveField, setDeclinedActiveField] = React.useState<'subject' | 'body'>('body');
    const declinedSubjectRef = React.useRef<HTMLInputElement>(null);
    const declinedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        Promise.all([moduleApi.getPrestationNotificationSettings(), moduleApi.getClientNotificationSettings()])
            .then(([ps, cs]: [any, any]) => {
                setDevisSubject(ps?.devis_subject || '');
                setDevisBody(ps?.devis_body || '');
                setDevisButtonLabel(ps?.devis_button_label || '');
                setRelanceSubject(ps?.devis_relance_subject || '');
                setRelanceBody(ps?.devis_relance_body || '');
                setNotifySigned(cs ? !!cs.notify_devis_signed_received : true);
                setSignedSubject(cs?.devis_signed_received_subject || '');
                setSignedBody(cs?.devis_signed_received_body || '');
                setNotifyDeclined(cs ? !!cs.notify_devis_declined : true);
                setDeclinedSubject(cs?.devis_declined_subject || '');
                setDeclinedBody(cs?.devis_declined_body || '');
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const [psRes, csRes]: [any, any] = await Promise.all([
                moduleApi.patchPrestationNotificationSettings('devis', { devis_subject: devisSubject, devis_body: devisBody, devis_button_label: devisButtonLabel, devis_relance_subject: relanceSubject, devis_relance_body: relanceBody }),
                moduleApi.patchClientNotificationSettings('devis_prestation', {
                    notify_devis_signed_received: notifySigned, devis_signed_received_subject: signedSubject, devis_signed_received_body: signedBody,
                    notify_devis_declined: notifyDeclined, devis_declined_subject: declinedSubject, devis_declined_body: declinedBody,
                }),
            ]);
            if (psRes?.error || csRes?.error) { setErreur(psRes?.error || csRes?.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    const champ = 'w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
    const onglets: { key: 'client' | 'etablissement'; label: string }[] = [
        { key: 'client', label: 'Vers le client' },
        { key: 'etablissement', label: "Vers l'établissement" },
    ];

    return (
        <FenetreEmailsModule embarque={embarque} titre="Paramètres des emails — Devis" icone={<FileText size={16} />} onClose={onClose}
            chargement={loading} erreur={erreur} enregistrer={enregistrer}
            etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'}
            avant={<Segmented value={onglet} options={onglets.map((o) => ({ value: o.key, label: o.label }))} onChange={(v) => setOnglet(v as 'client' | 'etablissement')} ariaLabel="Destinataire des emails" />}>
            {onglet === 'client' ? (
                <>
                    <NotificationBlock title="Envoi du devis" description="Part quand vous cliquez sur « Envoyer le devis ». « Renvoyer le devis » renvoie ce mail à l'identique."
                        extra={(
                            <div>
                                <label className="text-xs font-bold text-content-muted">Texte du bouton</label>
                                <input className={champ} value={devisButtonLabel} onChange={(e) => setDevisButtonLabel(e.target.value)} placeholder="Consulter et signer le devis" />
                            </div>
                        )}
                        subject={devisSubject} setSubject={setDevisSubject} body={devisBody} setBody={setDevisBody}
                        subjectRef={devisSubjectRef} bodyRef={devisBodyRef}
                        activeField={devisActiveField} setActiveField={setDevisActiveField}
                        tokens={DEVIS_CLIENT_TOKENS}
                        onInsertToken={(t) => devisActiveField === 'subject'
                            ? insertNotificationToken(devisSubjectRef.current, devisSubject, setDevisSubject, t)
                            : insertNotificationToken(devisBodyRef.current, devisBody, setDevisBody, t)} />
                    <NotificationBlock title="Relance du devis" description="Part quand vous cliquez sur « Relancer le client » — sans le PDF, le lien suffit."
                        subject={relanceSubject} setSubject={setRelanceSubject} body={relanceBody} setBody={setRelanceBody}
                        subjectRef={relanceSubjectRef} bodyRef={relanceBodyRef}
                        activeField={relanceActiveField} setActiveField={setRelanceActiveField}
                        tokens={DEVIS_CLIENT_TOKENS}
                        onInsertToken={(t) => relanceActiveField === 'subject'
                            ? insertNotificationToken(relanceSubjectRef.current, relanceSubject, setRelanceSubject, t)
                            : insertNotificationToken(relanceBodyRef.current, relanceBody, setRelanceBody, t)} />
                </>
            ) : (
                <>
                    <NotificationBlock title="Devis signé reçu" description="Reçu par l'établissement quand le client signe depuis le lien du devis."
                        toggle={{ checked: notifySigned, onChange: setNotifySigned }}
                        subject={signedSubject} setSubject={setSignedSubject} body={signedBody} setBody={setSignedBody}
                        subjectRef={signedSubjectRef} bodyRef={signedBodyRef}
                        activeField={signedActiveField} setActiveField={setSignedActiveField}
                        tokens={DEVIS_SIGNED_TOKENS}
                        onInsertToken={(t) => signedActiveField === 'subject'
                            ? insertNotificationToken(signedSubjectRef.current, signedSubject, setSignedSubject, t)
                            : insertNotificationToken(signedBodyRef.current, signedBody, setSignedBody, t)} />
                    <NotificationBlock title="Devis refusé" description="Reçu par l'établissement quand le client refuse le devis."
                        toggle={{ checked: notifyDeclined, onChange: setNotifyDeclined }}
                        subject={declinedSubject} setSubject={setDeclinedSubject} body={declinedBody} setBody={setDeclinedBody}
                        subjectRef={declinedSubjectRef} bodyRef={declinedBodyRef}
                        activeField={declinedActiveField} setActiveField={setDeclinedActiveField}
                        tokens={DEVIS_DECLINED_TOKENS}
                        onInsertToken={(t) => declinedActiveField === 'subject'
                            ? insertNotificationToken(declinedSubjectRef.current, declinedSubject, setDeclinedSubject, t)
                            : insertNotificationToken(declinedBodyRef.current, declinedBody, setDeclinedBody, t)} />
                </>
            )}
        </FenetreEmailsModule>
    );
}
